import { Calendar } from 'lucide-react'
import { formatDate, type DateFormat } from '@/lib/utils/ui-helpers'

/**
 * Props for the NodeDate component
 */
interface NodeDateProps {
  /**
   * The due date to display (Date object or string)
   */
  dueDate: Date | string
  /**
   * Optional label to display before the due date (defaults to "Due:")
   */
  label?: string
  /**
   * Format style for the date display (defaults to 'long')
   */
  format?: DateFormat
  /**
   * Size variant for the display (defaults to 'regular')
   */
  size?: 'sm' | 'regular'
  /**
   * Whether to show overdue styling (red color) for past dates (defaults to true)
   */
  showOverdue?: boolean
  /**
   * Whether to stack the label above the content instead of inline (defaults to false)
   */
  labelStacked?: boolean
  /**
   * Additional CSS classes for the container
   */
  className?: string
}

/**
 * A reusable component for displaying node due dates with consistent styling
 *
 * @example
 * ```tsx
 * // Basic usage with long format (default) - shows overdue styling
 * <NodeDate dueDate={new Date()} />
 *
 * // Small size variant for tasks - shows overdue styling
 * <NodeDate dueDate={task.dueDate} size="sm" />
 *
 * // Message creation date - no overdue styling
 * <NodeDate dueDate={message.createdAt} showOverdue={false} />
 *
 * // Document update date - no overdue styling
 * <NodeDate dueDate={document.updatedAt} showOverdue={false} />
 *
 * // Regular size variant (explicit)
 * <NodeDate dueDate="2024-12-31" size="regular" />
 *
 * // With custom label (inline layout)
 * <NodeDate dueDate="2024-12-31" label="Deadline:" />
 *
 * // With stacked label layout
 * <NodeDate dueDate="2024-12-31" label="Deadline:" labelStacked />
 *
 * // With different format
 * <NodeDate
 *   dueDate={task.dueDate}
 *   format="medium"
 *   label="Due:"
 * />
 *
 * // With custom styling
 * <NodeDate
 *   dueDate="2024-12-31"
 *   format="short"
 *   className="text-lg"
 * />
 *
 * // Stacked layout with custom styling
 * <NodeDate
 *   dueDate="2024-12-31"
 *   label="Due Date:"
 *   labelStacked
 *   className="w-full"
 * />
 * ```
 */
export function NodeDate({
  dueDate,
  label = 'Due:',
  format = 'long',
  size = 'regular',
  showOverdue = true,
  labelStacked = false,
  className = '',
}: NodeDateProps) {
  const formattedDate = formatDate(dueDate, format)

  if (!formattedDate) {
    return null
  }

  // Check if the due date is overdue (only if showOverdue is enabled)
  const dueDateObj = new Date(dueDate)
  const now = new Date()
  const isOverdue = showOverdue && dueDateObj < now

  const sizeClasses = size === 'sm' ? 'text-sm' : ''
  const overdueClasses = isOverdue ? 'text-red-600' : ''
  const flexDirection = labelStacked ? 'flex-col items-start gap-1' : 'flex items-center gap-2'

  return (
    <div className={`${flexDirection} ${sizeClasses} ${className}`}>
      {label && (
        <span className={`text-muted-foreground ${labelStacked ? 'mb-1' : ''}`}>{label}</span>
      )}
      <div className="flex items-center gap-1">
        <Calendar className={`h-4 w-4 ${overdueClasses}`} />
        <span className={overdueClasses}>{formattedDate}</span>
      </div>
    </div>
  )
}
